import {
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  message: ReactNode;
  children: ReactNode;
  /** aria-label do trigger — obrigatório quando `children` não tem texto próprio (ex.: só um ícone). */
  triggerLabel?: string;
  triggerClassName?: string;
  /**
   * 'click' (default): o trigger é um `<button>` próprio — usado quando
   * `children` não é interativo (ex.: só um ícone de info).
   * 'hover': o trigger é um `<span>` que só envolve `children`, abrindo no
   * hover/foco — usado quando `children` já é um elemento interativo com seu
   * próprio `onClick` (não dá pra aninhar `<button>` dentro de `<button>`).
   */
  trigger?: 'click' | 'hover';
}

const GAP = 8;
const VIEWPORT_MARGIN = 8;
const ARROW_SIZE = 8;
const ARROW_MARGIN = 10;

interface Coords {
  top: number;
  left: number;
  arrowLeft: number;
}

/**
 * Tooltip padrão da plataforma — usar sempre este em vez de `title` nativo
 * ou balões ad-hoc, para manter tamanho/posição/estilo consistentes.
 *
 * Renderiza via portal com posição `fixed`, necessário porque este
 * componente é usado dentro de tabelas com scroll horizontal, onde um balão
 * `absolute` seria cortado pelo container. A largura acompanha o conteúdo
 * (até um teto) em vez de fixa, para não inflar tooltips de texto curto.
 */
export function Tooltip({
  message,
  children,
  triggerLabel,
  triggerClassName,
  trigger = 'click',
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const setTriggerRef = (el: HTMLElement | null) => {
    triggerRef.current = el;
  };
  const popoverRef = useRef<HTMLDivElement>(null);

  // Passo 1: assim que abre, ancora no trigger (a largura real do balão
  // ainda não existe no DOM para centralizar com precisão).
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setCoords(null);
      return;
    }
    const t = triggerRef.current.getBoundingClientRect();
    setCoords({ top: t.bottom + GAP, left: t.left, arrowLeft: ARROW_MARGIN });
  }, [open]);

  // Passo 2: com o balão já montado, mede a largura real e corrige a
  // posição (centralizada no trigger, clampada na viewport) + a seta.
  // Roda em cascata dentro da mesma fase de layout, antes do paint — sem piscar.
  useLayoutEffect(() => {
    if (!open || !coords || !popoverRef.current || !triggerRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const p = popoverRef.current.getBoundingClientRect();
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, t.left + t.width / 2 - p.width / 2),
      window.innerWidth - p.width - VIEWPORT_MARGIN,
    );
    const top = t.bottom + GAP;
    const arrowLeft = Math.min(
      Math.max(ARROW_MARGIN, t.left + t.width / 2 - left - ARROW_SIZE / 2),
      p.width - ARROW_SIZE - ARROW_MARGIN,
    );
    if (
      left !== coords.left ||
      top !== coords.top ||
      arrowLeft !== coords.arrowLeft
    ) {
      setCoords({ top, left, arrowLeft });
    }
  }, [open, coords]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    // capture:true porque o scroll da tabela não faz bubbling até a window
    const handleScroll = () => setOpen(false);

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  const popover =
    open &&
    coords &&
    createPortal(
      <div
        ref={popoverRef}
        role="tooltip"
        style={{ top: coords.top, left: coords.left }}
        className="border-(--color-border) bg-(--color-bg-primary) text-(--color-text-secondary) fixed z-50 w-max max-w-72 rounded-lg border px-3 py-2 text-xs leading-relaxed shadow-lg"
      >
        <span
          style={{ left: coords.arrowLeft }}
          className="border-(--color-border) bg-(--color-bg-primary) absolute -top-1 h-2 w-2 rotate-45 border-l border-t"
        />
        {message}
      </div>,
      document.body,
    );

  if (trigger === 'hover') {
    return (
      <span
        ref={setTriggerRef}
        className={triggerClassName ?? 'inline-flex'}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
        {popover}
      </span>
    );
  }

  return (
    <>
      <button
        ref={setTriggerRef}
        type="button"
        aria-label={triggerLabel}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={triggerClassName ?? 'inline-flex'}
      >
        {children}
      </button>

      {popover}
    </>
  );
}
